import { z } from 'zod';
import {
  BLOCK_BOX_SPEC,
  BLOCK_SPECS,
  BLOCK_TYPES,
  BLOCKS_LAYOUT_VERSION,
  BLOCKS_MAX,
  BLOCKS_MAX_COLUMNS,
  BLOCKS_MAX_ROWS,
  BLOCKS_MAX_SPAN_ROWS,
  type BlockFieldSpec,
  type Block,
  type BlockType,
} from '../menu/blocks.js';

/**
 * Validation for a board's "Build your own" layout. Built from the same field table the
 * renderer sanitises with (BLOCK_SPECS), so a bound changes in one place: the server refuses
 * what the renderer would have clamped.
 */

const color = z
  .string()
  .regex(/^(#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})|theme:(bg|surface|text|primary|accent))$/i, 'Use a hex colour or a theme colour');
// Section and item ids are chosen by the client (sections) or are uuids (items); short either way.
const ref = z.string().trim().min(1).max(64);

function field(spec: BlockFieldSpec): z.ZodType {
  let schema: z.ZodType;
  switch (spec.t) {
    case 'num': {
      const n = z.number().finite().min(spec.min).max(spec.max);
      return spec.int ? n.int() : n;
    }
    case 'enum':
      return z.enum(spec.values as [string, ...string[]]);
    case 'bool':
      return z.boolean();
    case 'color':
      schema = color;
      break;
    case 'text':
      schema = z.string().max(spec.max);
      break;
    case 'ref':
      schema = ref;
      break;
    case 'media':
      schema = z.string().uuid();
      break;
  }
  return spec.nullable ? schema.nullable() : schema;
}

function fields(specs: Record<string, BlockFieldSpec>) {
  return Object.fromEntries(Object.entries(specs).map(([key, spec]) => [key, field(spec)]));
}

const base = {
  id: z.string().trim().min(1).max(40),
  colSpan: z.number().int().min(1).max(BLOCKS_MAX_COLUMNS),
  rowSpan: z.number().int().min(1).max(BLOCKS_MAX_SPAN_ROWS),
  box: z.object(fields(BLOCK_BOX_SPEC)),
};

const itemSource = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('item'), itemId: ref }),
  z.object({ kind: z.literal('next'), sectionId: ref.nullable() }),
]);

const blockSchemas = BLOCK_TYPES.map((type: BlockType) =>
  z.object({
    ...base,
    type: z.literal(type),
    ...fields(BLOCK_SPECS[type]),
    ...(type === 'item' ? { source: itemSource } : {}),
  }),
);

// Built from a table, so its inferred type is loose; it validates exactly the `Block` shape.
export const blockSchema = z.discriminatedUnion('type', blockSchemas as unknown as [z.ZodObject, ...z.ZodObject[]]) as unknown as z.ZodType<Block>;

const rows = z.number().int().min(1).max(BLOCKS_MAX_ROWS).nullable();

export const blocksLayoutSchema = z
  .object({
    version: z.literal(BLOCKS_LAYOUT_VERSION),
    columns: z.number().int().min(1).max(BLOCKS_MAX_COLUMNS),
    portraitColumns: z.number().int().min(1).max(BLOCKS_MAX_COLUMNS),
    rows,
    portraitRows: rows,
    gap: z.number().finite().min(0).max(120),
    padding: z.number().finite().min(0).max(200),
    background: z.object({ color: color.nullable(), mediaId: z.string().uuid().nullable(), overlay: z.number().min(0).max(0.9) }),
    blocks: z.array(blockSchema).max(BLOCKS_MAX),
  })
  .refine((layout) => new Set(layout.blocks.map((b) => b.id)).size === layout.blocks.length, {
    message: 'Two blocks share an id',
    path: ['blocks'],
  });
